import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { LeagueSwitcher } from '../../components/HomeSelector';
import LORCommandCenter from './LORCommandCenter';
import LORStandings from './LORStandings';
import LORPowerRankings from './LORPowerRankings';
import LORHistory from './LORHistory';
import LORMatchups from './LORMatchups';
import LORCommissioner from './LORCommissioner';
// Phase 2 components
import LORWeeklyIntel from './LORWeeklyIntel';
import LORTeamBeat from './LORTeamBeat';
import CommissionerWarRoom from '../CommissionerWarRoom';
import LORStartSit from './LORStartSit';
import LORWaivers from './LORWaivers';
import LORMatchupPreviews from './LORMatchupPreviews';
import LORPlayoffOdds from './LORPlayoffOdds';
import LORByePlanner from './LORByePlanner';
import LORStreamerHub from './LORStreamerHub';
import LORTradeAnalyzer from './LORTradeAnalyzer';
// Phase 3 components
import LORManagers from './LORManagers';
import LORH2H from './LORH2H';
import LORRivalries from './LORRivalries';
import LORRecords from './LORRecords';
import LORHallOfChampions from './LORHallOfChampions';
import LORHallOfFame from './LORHallOfFame';
// Phase 4 components
import LORKeeperTools from './LORKeeperTools';
import LORMockDraft from './LORMockDraft';
import LORDraftOrder from './LORDraftOrder';
import ADPPage from '../ADPPage';
// Lore system
import LORLore from './LORLore';
import LORTheTrade from './LORTheTrade';
import LORCommissionerLegacy from './LORCommissionerLegacy';
import LORFoundingMembers from './LORFoundingMembers';
import LORMilestones from './LORMilestones';
import LORBylaws from './LORBylaws';
import LeagueIntelDesk from '../LeagueIntelDesk';
import LeagueIntelAssistant from '../../components/LeagueIntelAssistant';
import SiteTelemetry from '../../components/SiteTelemetry';
import Icon from '../../components/Icon';
import { resolveFranchiseTheme } from '../../utils/franchiseThemes';
import './LORAppShell.css';
import './LOR.css';

const API = process.env.REACT_APP_API_URL || '';

const NAV_GROUPS = [
  {
    key: 'home',
    label: 'Command Center',
    icon: '🏆',
    paths: [{ label: 'Command Center', to: '/league-of-record' }],
  },
  {
    key: 'league',
    label: 'League',
    paths: [
      { label: 'Standings',      to: '/league-of-record/standings',     flagKey: 'lor_standings' },
      { label: 'Power Rankings', to: '/league-of-record/power-rankings', flagKey: 'lor_power_rankings' },
      { label: 'Playoff Odds',   to: '/league-of-record/playoff-odds',   flagKey: 'lor_playoff_odds' },
      { label: 'Matchups',       to: '/league-of-record/matchups' },
      { label: 'Matchup Preview',to: '/league-of-record/preview',        flagKey: 'lor_matchup_previews' },
    ],
  },
  {
    key: 'history',
    label: 'History',
    paths: [
      { label: 'History Archive',   to: '/league-of-record/history',         flagKey: 'lor_history_archive' },
      { label: 'Manager Records',   to: '/league-of-record/managers',        flagKey: 'lor_history_archive' },
      { label: 'Hall of Champions', to: '/league-of-record/hall-of-champions', flagKey: 'lor_hall_of_champions' },
      { label: 'Hall of Fame',      to: '/league-of-record/hall-of-fame',      flagKey: 'lor_hall_of_champions' },
    ],
  },
  {
    key: 'lore',
    label: '📜 Lore',
    paths: [
      { label: 'Timeline',              to: '/league-of-record/lore' },
      { label: 'The Trade',             to: '/league-of-record/the-trade' },
      { label: 'Commissioner Legacy',   to: '/league-of-record/commissioner-legacy' },
      { label: 'Founding Members',      to: '/league-of-record/founding-members' },
      { label: 'Milestones',            to: '/league-of-record/milestones' },
    ],
  },
  {
    key: 'intel',
    label: 'Intel',
    paths: [
      { label: 'Weekly Intel', to: '/league-of-record/intel',    flagKey: 'lor_weekly_intel' },
      { label: 'Team Beat', to: '/league-of-record/team-beat', flagKey: 'lor_history_archive' },
      { label: 'Rivalries',    to: '/league-of-record/rivalries', flagKey: 'lor_rivalry_engine' },
      { label: 'Record Book',  to: '/league-of-record/records',  flagKey: 'lor_record_book' },
      { label: 'Head-to-Head', to: '/league-of-record/h2h',      flagKey: 'lor_rivalry_engine' },
    ],
  },
  {
    key: 'tools',
    label: 'Tools',
    paths: [
      { label: 'ADP',            to: '/league-of-record/adp' },
      { label: 'Trade Analyzer', to: '/league-of-record/trade-analyzer' },
      { label: 'Waiver Wire',    to: '/league-of-record/waivers',   flagKey: 'lor_waiver_targets' },
      { label: 'Start / Sit',    to: '/league-of-record/startsit',  flagKey: 'lor_start_sit' },
      { label: 'Bye Planner',    to: '/league-of-record/bye',       flagKey: 'lor_bye_week_planner' },
      { label: 'Streamer Hub',   to: '/league-of-record/streamers', flagKey: 'lor_streamer_hub' },
    ],
  },
  {
    key: 'keepers',
    label: 'Keepers',
    paths: [
      { label: 'Keeper Tools', to: '/league-of-record/keepers',     flagKey: 'lor_keeper_tools' },
      { label: 'Mock Draft',   to: '/league-of-record/mock-draft',  flagKey: 'lor_mock_draft'   },
      { label: 'Draft Order',  to: '/league-of-record/draft-order', flagKey: 'lor_keeper_tools' },
    ],
  },
  {
    key: 'bylaws',
    label: '📜 By-Laws',
    paths: [{ label: 'By-Laws', to: '/league-of-record/bylaws' }],
  },
];

const COMMISSIONER_GROUP = {
  key: 'commissioner',
  label: 'Commissioner',
  commissioner: true,
  paths: [{ label: 'LOR Commissioner', to: '/league-of-record/commissioner' },
    { label: '😈 War Room', to: '/league-of-record/war-room' }],
};

export default function LORAppShell() {
  const { currentUser } = useUser();
  const location        = useLocation();
  const navigate        = useNavigate();
  const [status, setStatus] = useState(null);
  const [theme, setTheme]   = useState(() => localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') root.setAttribute('data-theme', 'light');
    else root.removeAttribute('data-theme');
  }, [theme]);

  useEffect(() => {
    fetch(`${API}/api/lor/status`).then(r => r.json()).then(setStatus).catch(() => {});
  }, []);

  const isCommissioner =
    currentUser?.display_name === 'PhillyxCommish' ||
    currentUser?.username     === 'PhillyxCommish';
  const franchiseTheme = resolveFranchiseTheme(currentUser);
  const isLeagueMember = !currentUser?.guest && (currentUser?.memberships || []).some(membership => membership.kind === 'lor');

  const flags      = status?.feature_flags || {};
  const flagVisible = (p) => !p.flagKey || flags[p.flagKey] !== false;

  const allGroups = [...NAV_GROUPS, ...(isCommissioner ? [COMMISSIONER_GROUP] : [])];

  const activeGroup = allGroups.find(g =>
    g.paths.some(p =>
      p.to === '/league-of-record'
        ? location.pathname === '/league-of-record' || location.pathname === '/league-of-record/'
        : location.pathname.startsWith(p.to)
    )
  ) || null;

  const visibleSubs = activeGroup
    ? activeGroup.paths.filter(p => (!p.memberOnly || isLeagueMember) && flagVisible(p))
    : [];

  const handlePrimaryClick = (group) => {
    const first = group.paths.find(p => (!p.memberOnly || isLeagueMember) && flagVisible(p));
    if (first) navigate(first.to);
  };

  const avatarUrl = currentUser?.avatar
    ? `https://sleepercdn.com/avatars/thumbs/${currentUser.avatar}`
    : null;

  return (
    <div
      className={`lor-app${franchiseTheme ? ` franchise-theme franchise-theme--${franchiseTheme.id}` : ''}`}
      data-franchise-theme={franchiseTheme?.id || undefined}
      style={franchiseTheme ? { '--franchise-logo': `url(${franchiseTheme.logoUrl})` } : undefined}
    >
      <header className="lor-header">
        {/* ── Brand bar: identity · league switcher ── */}
        <div className="lor-header-brand">
          <div className="lor-league-title">
            <div className="lor-logo-mark"><Icon emoji="🏆" size={22} /></div>
            <div>
              <h1>League Intel</h1>
              <span className="lor-subtitle">
                League of Record · Full PPR · SuperFlex · 2 Keepers
              </span>
            </div>
          </div>
          <div className="lor-header-brand-right">
            {franchiseTheme && (
              <div className="franchise-lockup" aria-label={`${franchiseTheme.label} fan theme`}>
                <img src={franchiseTheme.logoUrl} alt="" className="franchise-lockup-logo" />
                <span><small>Fan Mode</small>{franchiseTheme.label}</span>
              </div>
            )}
            <LeagueSwitcher current="lor" />
          </div>
        </div>

        {/* ── Ops bar: theme · user ── */}
        <div className="lor-header-ops">
          <button
            className="lor-theme-toggle"
            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? <Icon emoji="🌙" size={18} /> : <Icon emoji="☀️" size={18} />}
          </button>
          <div className="lor-user-pill">
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" className="lor-user-avatar" />
              : <span className="lor-user-avatar-placeholder"><Icon emoji="👤" size={20} /></span>}
            <span className="lor-user-name">
              {currentUser?.guest ? 'Guest' : currentUser?.display_name}
            </span>
          </div>
        </div>

        <nav className="lor-nav-primary">
          <div className="lor-nav-primary-main">
            {allGroups.filter(g => !g.commissioner).map(group => (
              <button
                key={group.key}
                data-telemetry={`nav:${group.key}`}
                className={`lor-nav-tab${activeGroup?.key === group.key ? ' active' : ''}`}
                onClick={() => handlePrimaryClick(group)}
              >
                {group.icon && <Icon emoji={group.icon} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}{group.label}
              </button>
            ))}
          </div>
          {isCommissioner && (
            <button
              className={`lor-nav-tab lor-nav-tab--commissioner${activeGroup?.key === 'commissioner' ? ' active' : ''}`}
              onClick={() => handlePrimaryClick(COMMISSIONER_GROUP)}
            >
              <Icon emoji="🔒" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Commissioner
            </button>
          )}
        </nav>

        {visibleSubs.length > 1 && (
          <nav className="lor-nav-sub">
            {visibleSubs.map(p => (
              <NavLink
                key={p.to}
                data-telemetry={`subnav:${p.to}`}
                to={p.to}
                end={p.to === '/league-of-record'}
                className={({ isActive }) =>
                  `lor-nav-link${isActive ? ' active' : ''}${p.comingSoon ? ' coming-soon' : ''}`
                }
              >
                {p.label}
                {p.comingSoon && <span className="lor-soon-badge">Soon</span>}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="lor-main">
        <Routes>
          <Route index                element={<LORCommandCenter api={API} status={status} />} />
          <Route path="standings"     element={<LORStandings api={API} />} />
          <Route path="power-rankings" element={<LORPowerRankings api={API} />} />
          <Route path="history"       element={<LORHistory api={API} />} />
          <Route path="matchups"      element={<LORMatchups api={API} status={status} />} />
          <Route path="commissioner"  element={<LORCommissioner api={API} />} />
          <Route path="war-room" element={<CommissionerWarRoom api={API} />} />
          {/* Phase 2 */}
          <Route path="intel"         element={<LORWeeklyIntel api={API} />} />
          <Route path="league-desk"   element={<LeagueIntelDesk api={API} league="lor" />} />
          <Route path="team-beat" element={<LORTeamBeat api={API} />} />
          <Route path="startsit"      element={<LORStartSit api={API} />} />
          <Route path="waivers"       element={<LORWaivers api={API} />} />
          <Route path="preview"       element={<LORMatchupPreviews api={API} status={status} />} />
          <Route path="playoff-odds"  element={<LORPlayoffOdds api={API} />} />
          <Route path="adp"           element={<ADPPage api={API} />} />
          <Route path="trade-analyzer" element={<LORTradeAnalyzer api={API} />} />
          <Route path="bye"           element={<LORByePlanner api={API} />} />
          <Route path="streamers"     element={<LORStreamerHub api={API} />} />
          {/* Phase 3 */}
          <Route path="managers"          element={<LORManagers api={API} />} />
          <Route path="h2h"               element={<LORH2H api={API} />} />
          <Route path="rivalries"         element={<LORRivalries api={API} />} />
          <Route path="records"           element={<LORRecords api={API} />} />
          <Route path="hall-of-champions" element={<LORHallOfChampions api={API} />} />
          <Route path="hall-of-fame"      element={<LORHallOfFame api={API} />} />
          {/* Phase 4 */}
          <Route path="keepers"      element={<LORKeeperTools api={API} />} />
          <Route path="mock-draft"   element={<LORMockDraft   api={API} />} />
          <Route path="draft-order"  element={<LORDraftOrder  api={API} />} />
          {/* Lore system */}
          <Route path="lore"                element={<LORLore api={API} />} />
          <Route path="the-trade"           element={<LORTheTrade api={API} />} />
          <Route path="commissioner-legacy" element={<LORCommissionerLegacy api={API} />} />
          <Route path="founding-members"    element={<LORFoundingMembers api={API} />} />
          <Route path="milestones"          element={<LORMilestones api={API} />} />
          <Route path="bylaws"              element={<LORBylaws />} />
        </Routes>
      </main>

      <footer className="lor-footer">
        <span>
          <Icon emoji="🏆" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> League of Record · Est. 2012 · Full PPR · SuperFlex · Powered by Sleeper API · LeagueIntel
        </span>
      </footer>
      <LeagueIntelAssistant api={API} league="lor" enabled={Boolean(isLeagueMember)} />
      <SiteTelemetry api={API} league="lor" enabled={!currentUser?.guest} />
    </div>
  );
}

function LORComingSoon({ name, phase }) {
  return (
    <div className="lor-coming-soon">
      <div className="lor-coming-soon-icon">⚙️</div>
      <h2>{name}</h2>
      <p>Coming in Phase {phase}.</p>
      <p className="lor-coming-soon-sub">
        The League of Record's history runs deep. This feature is being built to match.
      </p>
    </div>
  );
}
